// ligfrontend/src/components/Lig/LigOutreachComponent.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { memberAPI } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ConfirmDialog from "../common/ConfirmDialog";
import "./LigOutreachComponent.css";

const LigOutreachComponent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [email, setEmail] = useState("");
  const [nickName, setNickName] = useState("");
  const [emailValid, setEmailValid] = useState(false);
  const [family, setFamily] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState({
    isOpen: false,
    message: "",
  });

  useEffect(() => {
    loadFamily();
  }, []);

  const loadFamily = async () => {
    try {
      setLoading(true);
      const familyData = await memberAPI.getFamily();
      setFamily(familyData);
    } catch (error) {
      console.error("Error loading family:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (emailString) => {
    const regexp =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regexp.test(emailString);
  };

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);

    if (emailValue.length > 6) {
      const isValid = validateEmail(emailValue);

      if (isValid) {
        // Check if email already exists in family
        const emailExists = family.some((member) =>
          member.email.toLowerCase().includes(emailValue.toLowerCase())
        );
        setEmailValid(!emailExists);
      } else {
        setEmailValid(false);
      }
    } else {
      setEmailValid(false);
    }
  };

  const handleNickNameChange = (e) => {
    setNickName(e.target.value);
  };

  const handleInvite = async () => {
    if (!emailValid || !nickName.trim()) return;

    try {
      setLoading(true);
      const tempMember = await memberAPI.invite(
        email,
        user.name || user.uname,
        nickName
      );

      setSuccessDialog({
        isOpen: true,
        message: `${tempMember.email} has been invited to Life is Good!`,
      });

      resetForm();
    } catch (error) {
      console.error("Error sending invitation:", error);
      setSuccessDialog({
        isOpen: true,
        message: "Failed to send invitation. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setNickName("");
    setEmailValid(false);
  };

  const closeSuccessDialog = () => {
    setSuccessDialog({
      isOpen: false,
      message: "",
    });
  };

  const isFormValid = emailValid && nickName.trim().length > 0;

  if (loading && family.length === 0) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <>
      <div className="ligoutreach-container">
        <div className="ligoutreach-header">
          <button className="back-button" onClick={() => navigate("/lig")}>
            ← My Family
          </button>
        </div>

        <div className="outreach-card">
          <div className="outreach-icon">✉️</div>
          <h1 className="outreach-title">Invite Your Family Member</h1>
          <p className="outreach-subtitle">
            Share the joy of documenting life's good moments with your loved
            ones
          </p>

          <div className="outreach-form">
            <div className="form-group">
              <label htmlFor="nickName" className="form-label">
                Nickname
              </label>
              <input
                type="text"
                id="nickName"
                className="form-input"
                placeholder="Enter their nickname"
                value={nickName}
                onChange={handleNickNameChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                className={`form-input ${
                  email.length > 0 ? (emailValid ? "valid" : "invalid") : ""
                }`}
                placeholder="Enter their email address"
                value={email}
                onChange={handleEmailChange}
              />
              {email.length > 0 && !emailValid && (
                <span className="form-error">
                  {email.length <= 6
                    ? "Email is too short"
                    : family.some((m) =>
                        m.email.toLowerCase().includes(email.toLowerCase())
                      )
                    ? "This email is already in your family"
                    : "Please enter a valid email address"}
                </span>
              )}
              {emailValid && (
                <span className="form-success">✓ Email looks good!</span>
              )}
            </div>

            <button
              className="btn-invite"
              disabled={!isFormValid || loading}
              onClick={handleInvite}
            >
              {loading ? "Sending Invitation..." : "Send Invitation"}
            </button>
          </div>

          <div className="outreach-info">
            <p className="info-text">
              💡 They'll receive an email with a link to join your family on
              Life is Good.
            </p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={successDialog.isOpen}
        title="Invitation Sent"
        message={successDialog.message}
        type="info"
        onConfirm={closeSuccessDialog}
        onCancel={closeSuccessDialog}
        confirmText="OK"
        cancelText=""
      />
    </>
  );
};

export default LigOutreachComponent;
